import React, { useContext } from 'react';
import { MobXProviderContext, observer } from 'mobx-react';

import classNames from 'classnames';

import { Sticky } from 'semantic-ui-react';

import '../css/CautionBanner.less';
import imageCaution from '../img/image-caution.png';

import { SatCoreComponent, SatCoreRegister } from '../SatCoreRegistry';
import UtilityService from '../services/UtilityService';

/**
 * CautionBanner - Displays warning/info messages at top of page
*
 * @param {boolean} allowMinimizeMaximize
 * @param {string} bannerKey
 * @param {string} cautionBannerMsg - (default) Direct HTML string message
 * @param {Array} cautionBannerMsgConfigArray - Advanced configuration for multiple messages or messages with action buttons
 * @param {boolean} isImpersonationCautionBannerShowing
 * @param {number} stickyOffset
 * @param {Function} t
 *
 * If cautionBannerMsg is provided, it takes precedence and cautionBannerMsgConfigArray is ignored.
 *
 * @example
 * // Simple usage with string message
 * <CautionBanner
 *   bannerKey="ExpiredLicense"
 *   cautionBannerMsg="Your license has expired."
 *   stickyOffset={50} />
 *
 * @example
 * // Advanced usage with config objects
 * <CautionBanner
 *   allowMinimizeMaximize={false}
 *   bannerKey="DefaultCautionBanner"
 *   cautionBannerMsgConfigArray={[
 *     {
 *       _id: 'expiredMsg',
 *       bannerMsgType: 'MESSAGE',
 *       translationKey: 'expiredLicenseCautionBannerMsg'
 *     },
 *     {
 *       _id: 'libraryMsg',
 *       actionConfig: {
 *         buttonLabelKey: 'clickHere',
 *         headerTitleKey: 'gettingStarted',
 *         iframeModalLauncherObj
 *       },
 *       actionType: 'IFRAME_MODAL_LAUNCHER',
 *       bannerMsgType: 'MESSAGE_WITH_ACTION',
 *       translationKey: 'libraryGettingStartedBannerMsg'
 *     }
 *   ]}
 *   stickyOffset={50}
 *   t={t} />
 */
const CautionBanner = observer(({
  allowMinimizeMaximize = true,
  bannerKey,
  cautionBannerMsg,
  cautionBannerMsgConfigArray,
  isImpersonationCautionBannerShowing,
  stickyOffset,
  t
} = {}) => {
  const { notificationManager } = useContext(MobXProviderContext);
  const BasicIframeModalLauncherButton = SatCoreComponent('BasicIframeModalLauncherButton');

  const renderCautionBannerContent = () => {
    // If cautionBannerMsg exists, use it (e.g. for ImpersonationCautionBanner, etc.)
    if (cautionBannerMsg) {
      return cautionBannerMsg;
    }

    // else, render from configuration objects (supports advanced messages with custom logic)
    if (!cautionBannerMsgConfigArray || cautionBannerMsgConfigArray.length === 0) {
      return null;
    }

    return cautionBannerMsgConfigArray?.map?.((config, index) => {
      const key = config._id || `banner-${index}`;

      if (config.bannerMsgType === 'MESSAGE') {
        return renderConfiguredCautionBannerMsg(config, key);
      } else if (config.bannerMsgType === 'MESSAGE_WITH_ACTION') {
        return renderConfiguredCautionBannerMsgWithAction(config, key);
      }
      return null;
    });
  };

  const renderConfiguredCautionBannerMsg = (config, key) => {
    const html = `${t(config.translationKey)}<br />`;
    const parsed = UtilityService.reactHtmlParserWrapper(html)?.parsed;
    return <React.Fragment key={key}>{parsed}</React.Fragment>;
  };

  const renderConfiguredCautionBannerMsgWithAction = (config, key) => {
    const messageHtml = t(config.translationKey);
    const messageParsed = UtilityService.reactHtmlParserWrapper(messageHtml)?.parsed;

    return (
      <div key={key} className='caution-banner-message-with-action'>
        <div className='caution-banner-message-text'>
          {messageParsed}
        </div>
        <div className='caution-banner-message-action'
          onClick={(event) => event.stopPropagation()}>
          {config.actionType === 'IFRAME_MODAL_LAUNCHER' && (
            <BasicIframeModalLauncherButton
              buttonLabel={t(config.actionConfig.buttonLabelKey)}
              headerTitle={t(config.actionConfig.headerTitleKey)}
              iframeModalLauncherObj={config.actionConfig.iframeModalLauncherObj} />
          )}
        </div>
      </div>
    );
  };

  const bannerTitle = notificationManager.isCautionBannerMinimized ? t('clickToExpand') : t('clickToMinimize');

  return !notificationManager.isCautionBannerLoading ? (
    <Sticky className={classNames('caution-banner-sticky', `${bannerKey}-sticky`, {
      isImpersonationCautionBannerShowing,
      'caution-banner-no-minimize': !allowMinimizeMaximize
    })} offset={stickyOffset || 50}>
      <div
        className={classNames('caution-banner-inner', `${bannerKey}-inner`, {
          'caution-banner-inner-minimized': notificationManager.isCautionBannerMinimized
        })}
        onClick={allowMinimizeMaximize ? () => {
          notificationManager.setIsCautionBannerLoading(true);
          notificationManager.setIsCautionBannerMinimized(!notificationManager.isCautionBannerMinimized);
          setTimeout(() => {
            notificationManager.setIsCautionBannerLoading(false);
          }, 1);
        } : undefined}
        title={allowMinimizeMaximize ? bannerTitle : undefined}>
        <div className='caution-banner-icon'>
          <img alt='' src={imageCaution} />
        </div>
        <div className='caution-banner-text'>
          {renderCautionBannerContent()}
        </div>
      </div>
    </Sticky>
  ) : null;
});
export default CautionBanner;

SatCoreRegister('CautionBanner', CautionBanner);
